import { Injectable } from "@angular/core";
import { PipeServiceAbstract } from "projects/alden-extension/src/lib/services/pipe-service/pipe-service-abstract";
import { Node } from "@alfresco/js-api";
import { CM_TITLE_PROP, UDEU_AUTHOR_LASTNAME1_PROP, UDEU_AUTHOR_LASTNAME2_PROP, UDEU_AUTHOR_NAME_PROP, UDEU_AUTHOR_RIA_PROP, UDEU_BOUD_NUMBER, UDEU_DOCUMENT_CODE_PROP, UDEU_DOCUMENT_DIRECTORY_PROP, UDEU_DOCUMENT_ORDER_PROP, UDEU_DOCUMENT_SIGNATURA_PROP, UDEU_DOCUMENT_TITLE_PROP, UDEU_LOCATION_SIGNATURA_PROP, UDEU_RECIPIENT_NAME_PROP, UDEU_STRUCTURAL_FOLDER_NAME_PROP } from "../model/contants/deusto";
import { ColumnValue } from "projects/alden-extension/src/lib/models/column-value";
import { RecordService } from "./record.service";
import { TransferService } from "./transfer.service";
import { LoanService } from "./loan.service";
import { CustomDataColumn } from "projects/alden-extension/src/lib/models/custom-viewer-config";
import { UdeuService } from "./udeu.service";
import { BoudService } from "./boud.service";

@Injectable({
  providedIn: 'root'
})
export class PipeServiceImpl extends PipeServiceAbstract { 

  authorsMap: { [uuid: string]: Node } = {};
  recipientsMap: { [uuid: string]: Node } = {};
  locationsMap: { [uuid: string]: Node } = {};
  loansMap: { [uuid: string]: Node } = {};
  documentsMap: { [uuid: string]: Node } = {};
  registersMap: { [uuid: string]: Node } = {};
  filesMap: { [uuid: string]: Node } = {};
  boudMap: { [uuid: string]: Node } = {};

  constructor(private udeuService: UdeuService, private recordService: RecordService, 
    private transferService: TransferService, private loanService: LoanService, private boudService: BoudService) {
    super();
  }
  
  override refreshMaps() {
    this.authorsMap = {};
    this.recipientsMap = {};
    this.locationsMap = {};
    this.loansMap = {};
    this.documentsMap = {};
    this.registersMap = {};
    this.filesMap = {};
    this.boudMap = {};
  }

  override async selectCustomPipe(column: CustomDataColumn, value: any, _elements?: string): Promise<ColumnValue> {
    const id = column.id;
    let columnValue: ColumnValue = { value, tooltip: value };
    if(id === "deusto-records-viewer.authorsUUIDs") {
      return this.selectEntitiesPipe(value, columnValue, this.authorsMap, this.recordService.getAuthorByUuid.bind(this.recordService), this.getAuthorString);
    } else if(id === "deusto-records-viewer.locationsUUIDs") {
      return this.selectEntitiesPipe(value, columnValue, this.locationsMap, this.recordService.getLocationByUuid.bind(this.recordService), this.getLocationString);
    } else if(id === "deusto-transfer-calendar-viewer.tcRecipientsUUIDs") {
      return this.selectEntitiesPipe(value, columnValue, this.recipientsMap, this.transferService.getRecipientByUuid.bind(this.transferService), this.getRecipientString);
    } else if(id === "deusto-transfers-viewer.documentsUUIDs") {
      return this.selectEntitiesPipe(value, columnValue, this.documentsMap, this.transferService.getDocumentByUuid.bind(this.transferService), this.getDocumentString);
    } else if(id === "deusto-records-viewer.loans" || id === "deusto-records-viewer.loans.borrowed") {
      return this.selectEntitiesPipe(value, columnValue, this.loansMap, this.loanService.getLoanByUuid.bind(this.loanService), this.getNameString);
    } else if(id === "deusto-loans-viewer.registers") {
      return this.selectEntitiesPipe(value, columnValue, this.registersMap, this.recordService.getRecordByUuid.bind(this.recordService), this.getNameString);
    } else if(id === "deusto-records-viewer.thesaurus") {
      return this.formatThesaurus(value, columnValue);
    } else if(id === "deusto-boud-viewer.boudRecordId") {
      return this.selectEntitiesPipe(value, columnValue, this.registersMap, this.recordService.getRecordByUuid.bind(this.recordService), this.getBoudNumberString); 
    } else if(id === "deusto-records-viewer.filesUUIDs") {
      return this.selectEntitiesPipe(value, columnValue, this.filesMap, this.udeuService.getFileByUuid.bind(this.udeuService), this.getNameString);
    } else if(column.key === "properties.udeu:filesUUIDs" 
      || id == "deusto-boud-viewer.EUContentUUID"
      || id == "deusto-boud-viewer.ESContentUUID"
      || id == "deusto-boud-viewer.ENContentUUID") {
      return this.selectEntitiesPipe(value, columnValue, this.filesMap, this.udeuService.getFileByUuid.bind(this.udeuService), this.getTitleString);
    } else if(id === "deusto-boud-viewer.boudMentionedElementsUUIDs" || id === "deusto-boud-viewer.boudModifiedElementsUUIDs" || id === "deusto-boud-viewer.boudRepealedElementsUUIDs"
      || id === "deusto-boud-viewer.boudSuspendedElementsUUIDs" || id === "deusto-boud-viewer.boudCorrectedElementsUUIDs"
    ) {
      return this.selectEntitiesPipe(value, columnValue, this.boudMap, this.boudService.getBoudByUuid.bind(this.boudService), this.getNameString);
    }

    value = value ? (Array.isArray(value) ? value.join(', ') : value) : '';
    const maxLength = 100;
    columnValue.value = value ? value.substring(0, maxLength) + (value.length > maxLength ? '...' : '') : '';
    return Promise.resolve(columnValue);
  }

  override selectCustomImagePipe(column: CustomDataColumn, value: any, _elements?: string) {
    const id = column.id;
    let columnValue: ColumnValue = { value, tooltip: null };
    if(id === "deusto-records-viewer.borrowed") { 
      columnValue.tooltip = "DEUSTO.IMAGES.BORROWED";
    } else if(id === "deusto-loans-viewer.renewal") {
      columnValue.tooltip = "DEUSTO.IMAGES.RENEWAL";
    }
    return Promise.resolve(columnValue);
  }

  private getAuthorString(node: any) : string {
    const entry = node.list.entries[0]?.entry;
    if(entry) {
      const properties = entry.properties;
      if(properties) {
        const ria = properties[UDEU_AUTHOR_RIA_PROP];
        const name = properties[UDEU_AUTHOR_NAME_PROP];
        const lastname1 = properties[UDEU_AUTHOR_LASTNAME1_PROP];
        const lastname2 = properties[UDEU_AUTHOR_LASTNAME2_PROP];
        return `${ria ? ria + '-' : ''}${name ?? ''} ${lastname1 ?? ''} ${lastname2 ?? ''}`.trim();
      }
    }
    return '';
  }

  private getLocationString(node: any) : string {
    const entry = node.list.entries[0]?.entry;
    if(entry) {
      const properties = entry.properties;
      const signatura = properties[UDEU_LOCATION_SIGNATURA_PROP];
      return `${signatura ?? ''}`.trim();
    }
    return '';
  }

  private getRecipientString(node: any) : string {
    const entry = node.list.entries[0]?.entry;
    if(entry) {
      const properties = entry.properties;
      const name = properties[UDEU_RECIPIENT_NAME_PROP];
      return `${name ?? ''}`.trim();
    }
    return '';
  }

  private getDocumentString(node: any) : string {
    const entry = node.list.entries[0]?.entry;
    if(entry) {
      let value = "";
      const properties = entry.properties;
      const code = properties[UDEU_DOCUMENT_CODE_PROP];
      const signatura = properties[UDEU_DOCUMENT_SIGNATURA_PROP];
      if(code && signatura) {
        value += `(${code}) ${signatura}`.trim() + ' - ';
      }

      const order = properties[UDEU_DOCUMENT_ORDER_PROP];
      const directory = properties[UDEU_DOCUMENT_DIRECTORY_PROP];
      const title = properties[UDEU_DOCUMENT_TITLE_PROP];
      value += `${order ?? directory ?? ''} / ${title ?? ''}`.trim();

      return value;
    }
    return '';
  }

  private getNameString(node: any) : string {
    const entry = node.list.entries[0]?.entry;
    let name = '';
    if(entry) {
      name = entry.name;
      const properties = entry.properties;
      if(properties) {
        name = properties[UDEU_STRUCTURAL_FOLDER_NAME_PROP] ?? name;
        const title = properties[CM_TITLE_PROP];
        name += title ? ` (${title})` : "";
      }
      return `${name ?? ''}`.trim();
    }
    return name;
  }

  private getTitleString(node: any) : string {
    const entry = node.list.entries[0]?.entry;
    if(entry) {
      const properties = entry.properties;
      if(properties) {
        const title = properties[CM_TITLE_PROP];
        return title;
      }
      return '';
    }
    return '';
  }

  private getBoudNumberString(node: any) : string {
    const entry = node.list.entries[0]?.entry;
    let number = '';
    if(entry) {
      const properties = entry.properties;
      if(properties) {
        number = properties[UDEU_BOUD_NUMBER];
      }
      return `${number ?? ''}`.trim();
    }
    return number;
  }

  private formatThesaurus(value: any, columnValue: ColumnValue) {
    const thesaurusUUIDs: string[] = value;
    let thesaurus: string[] = [];
  
    if (thesaurusUUIDs) {
      for (const entityUUID of thesaurusUUIDs) {
        thesaurus.push(entityUUID);
      }
    }
  
    thesaurus = thesaurus.filter((entity) => entity?.trim().length > 0);
    value = thesaurus.join(', ');
    const tooltip = thesaurus.join(',\n');

    const maxLength = 33;
    columnValue = { value: value ? value.substring(0, maxLength) + (value.length > maxLength ? '...' : '') : '', tooltip };
    return Promise.resolve(columnValue);
  }

}